import React, { useEffect, useRef, useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "./ui/drawer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { createLiveShare, pushLiveSharePosition, stopLiveShare } from "../lib/api";
import { useCountryConfig } from "../lib/useCountryConfig";
import { Radio, Copy, Share2, MessageSquare, Square } from "lucide-react";

const DURATIONS = [
  { hours: 1,   label: "1 h" },
  { hours: 3,   label: "3 h" },
  { hours: 8,   label: "8 h" },
  { hours: 24,  label: "24 h" },
];

const STORAGE_KEY = "rr_live_share_active";

// Iter89 — Update cadence gate: send when the sender has moved
// MIN_DISTANCE_M or more, OR MIN_INTERVAL_MS has elapsed since the last
// TRANSMITTED update — whichever comes first. Avoids hammering the
// backend every time watchPosition fires (which can be sub-second on
// some devices) while still sending a "heartbeat" on a stationary
// sender so the viewer never sees a falsely-stale badge.
const MIN_DISTANCE_M = 7;
const MIN_INTERVAL_MS = 5000;

const _haversineM = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/** Persist active token in localStorage so a reload keeps pushing. */
const loadActive = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch (_e) { return null; }
};
const saveActive = (v) => {
  try {
    if (v) localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (_e) { /* noop */ }
};

export const useLiveShareSession = (coords) => {
  const [active, setActive] = useState(() => loadActive());
  const intervalRef = useRef(null);
  const lastSentRef = useRef(null); // { lat, lng, ts }

  // Drive position pushes while a session is active and coords flow in.
  // Fires on every coords change (watchPosition) AND on a 5s heartbeat
  // interval (so a stationary sender still keeps the session "live") —
  // the cadence gate inside `tick()` is what actually decides whether a
  // given call results in a network request.
  const coordsRef = useRef(coords);
  useEffect(() => { coordsRef.current = coords; }, [coords]);

  // Iter89-fix — this effect must depend ONLY on the token, not on
  // `coords`. Depending on coords.latitude/longitude/accuracy re-runs
  // the effect on every single watchPosition callback (which can fire
  // multiple times per second on some devices), tearing down the
  // interval and resetting `lastSentRef` every time — which silently
  // defeats the cadence gate below and reintroduces exactly the
  // "PATCH every GPS tick" chattiness this gate exists to prevent.
  // Latest coords are read from `coordsRef` instead.
  useEffect(() => {
    if (!active?.token) return undefined;
    let cancelled = false;
    lastSentRef.current = null; // fresh gate only when the SESSION changes
    const tick = async () => {
      const coords = coordsRef.current;
      if (cancelled || !coords) return;
      const now = Date.now();
      const last = lastSentRef.current;
      if (last) {
        const dt = now - last.ts;
        const dist = _haversineM(last.lat, last.lng, coords.latitude, coords.longitude);
        if (dt < MIN_INTERVAL_MS && dist < MIN_DISTANCE_M) return; // neither threshold met yet
      }
      try {
        await pushLiveSharePosition(active.token, {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          heading: coords.heading,
          speed: coords.speed,
        });
        lastSentRef.current = { lat: coords.latitude, lng: coords.longitude, ts: now };
      } catch (e) {
        // 410 means expired/inactive — auto-end the session client-side.
        // Any OTHER error (network blip, e.g. Iter89 connectivity-loss
        // requirement) is swallowed here on purpose: the session stays
        // "active" locally and the NEXT tick (the 5s heartbeat below)
        // will simply try again with the latest valid position — no
        // new session is ever created.
        if (e?.response?.status === 410 || e?.response?.status === 404) {
          saveActive(null);
          setActive(null);
        }
      }
    };
    tick();
    intervalRef.current = setInterval(tick, MIN_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active?.token]);

  // Auto-stop when expiry passes
  useEffect(() => {
    if (!active?.expiresAt) return undefined;
    const ms = new Date(active.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      Promise.resolve().then(() => { saveActive(null); setActive(null); });
      return undefined;
    }
    const id = setTimeout(() => { saveActive(null); setActive(null); }, ms + 500);
    return () => clearTimeout(id);
  }, [active?.expiresAt]);

  const start = (token, expiresAt, label, publicUrl) => {
    const v = { token, expiresAt, label, publicUrl };
    saveActive(v);
    setActive(v);
  };
  const stop = async () => {
    if (active?.token) {
      try { await stopLiveShare(active.token); } catch (_e) { /* noop */ }
    }
    saveActive(null);
    setActive(null);
  };

  return { active, start, stop };
};

const buildPublicUrl = (token) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/live/${token}`;
};

const useCountdown = (expiresAt) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!expiresAt) return "—";
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return "Expiré";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export const LiveSharePanel = ({ open, onOpenChange, sessionApi, coords }) => {
  const { active, start, stop } = sessionApi;
  const cfg = useCountryConfig();
  const [label, setLabel] = useState("");
  const [hours, setHours] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const countdown = useCountdown(active?.expiresAt);

  const handleStart = async () => {
    if (!coords) {
      toast.error("GPS requis pour partager votre position");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createLiveShare({ label: label.trim() || null, durationHours: hours });
      const url = buildPublicUrl(res.token);
      // Push the first position immediately so the link works right away.
      try {
        await pushLiveSharePosition(res.token, {
          latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy,
        });
      } catch (_e) { /* noop */ }
      start(res.token, res.expires_at, res.label, url);
      toast.success("Partage en direct activé");
    } catch (e) {
      toast.error("Impossible de démarrer le partage");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async () => {
    await stop();
    toast.success("Partage arrêté");
  };

  const handleCopy = async () => {
    if (!active?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(active.publicUrl);
      toast.success("Lien copié");
    } catch (_e) {
      toast.error("Impossible de copier");
    }
  };

  const smsBody = active
    ? `Je partage ma position en direct sur ${(cfg.brand_name || "").toUpperCase()} : ${active.publicUrl}${active.label ? ` (${active.label})` : ""}`
    : "";

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="live-share-drawer">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Radio className="w-5 h-5 text-mali-red" /> Partage en direct
          </DrawerTitle>
          <DrawerDescription>
            Partagez votre position en temps réel avec un proche pendant une durée limitée.
            Le destinataire ouvre simplement le lien — aucune app requise.
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-2 space-y-4">
          {!active ? (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  Étiquette (optionnel)
                </label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex. Trajet vers le centre-ville"
                  maxLength={80}
                  data-testid="live-share-label"
                />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  Durée
                </div>
                <div className="grid grid-cols-4 gap-2" data-testid="live-share-duration-options">
                  {DURATIONS.map((d) => (
                    <button
                      key={d.hours}
                      type="button"
                      data-testid={`live-share-duration-${d.hours}h`}
                      onClick={() => setHours(d.hours)}
                      className={`py-2.5 rounded-xl border-2 font-bold text-sm transition-all ${
                        hours === d.hours
                          ? "border-mali-red bg-mali-red/10 text-mali-red"
                          : "border-border text-foreground hover:border-mali-red/40"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="live-share-active-card">
              <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 px-3.5 py-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="relative flex w-2.5 h-2.5">
                      <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
                      <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-emerald-600" />
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">
                      En direct
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-emerald-700 dark:text-emerald-300" data-testid="live-share-countdown">
                    {countdown}
                  </span>
                </div>
                {active.label && <div className="text-sm font-bold text-foreground">{active.label}</div>}
                <div className="font-mono text-[10px] text-muted-foreground break-all mt-1" data-testid="live-share-url">
                  {active.publicUrl}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="live-share-copy"
                  className="py-2.5 rounded-xl border-2 border-border text-foreground hover:border-mali-red/40 font-bold text-xs flex items-center justify-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier
                </button>
                <a
                  href={`sms:?&body=${encodeURIComponent(smsBody)}`}
                  data-testid="live-share-sms"
                  className="py-2.5 rounded-xl border-2 border-border text-foreground hover:border-mali-red/40 font-bold text-xs flex items-center justify-center gap-1.5"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> SMS
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(smsBody)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="live-share-whatsapp"
                  className="py-2.5 rounded-xl border-2 border-emerald-500/60 text-emerald-700 hover:bg-emerald-50 font-bold text-xs flex items-center justify-center gap-1.5"
                >
                  <Share2 className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>

        <DrawerFooter>
          {!active ? (
            <Button
              onClick={handleStart}
              disabled={submitting || !coords}
              data-testid="live-share-start-btn"
              className="bg-mali-red hover:bg-mali-red/90 text-white font-bold tracking-wide"
            >
              {submitting ? "Démarrage…" : "Démarrer le partage"}
            </Button>
          ) : (
            <Button
              onClick={handleStop}
              variant="outline"
              data-testid="live-share-stop-btn"
              className="border-2 border-mali-red text-mali-red font-bold tracking-wide hover:bg-mali-red/10"
            >
              <Square className="w-4 h-4 mr-1.5" /> Arrêter le partage
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="live-share-close">
            Fermer
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
